<template>
  <div class="container-fluid py-4">
    <div class="row starter-main mt-5 pt-5">
      <div class="col-sm-12">
        <!-- Header with Create Button -->
        <div class="d-flex justify-content-between align-items-center mb-4">
          <h4 class="mb-0">CMS Roles Management</h4>
          <button
            class="btn btn-primary btn-sm"
            @click="openCreateModal"
            :disabled="!canCreate('settings')"
          >
            <i class="fa fa-plus me-1"></i> Create Role
          </button>
        </div>

        <!-- Roles Table -->
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <CommonTable2
              :tableObj="tableConfig"
              :title="'CMS Roles'"
              @edit="handleEdit"
              @delete="handleDelete"
              @view="handleView"
              permissionKey="settings"
            />
            
            <!-- pagination -->
            <div class="d-flex justify-content-end align-items-center gap-3 mt-3 mb-2">
              <div class="d-flex align-items-center gap-2">
                <span class="text-muted">Rows per page:</span>
                <select
                  class="form-select form-select-sm rows-select"
                  v-model="tablePagination.per_page"
                  @change="changeRows"
                >
                  <option v-for="n in [5, 10, 20, 50, 100]" :key="n" :value="n">
                    {{ n }}
                  </option>
                </select>
              </div>
              <div class="pagination-mini">
                <button
                  class="page-btn prev_btn"
                  :disabled="!tablePagination.has_prev"
                  @click="handlePageChange(tablePagination.current_page - 1)"
                >«</button>
                <button
                  v-for="(p, index) in visiblePages"
                  :key="index"
                  class="page-btn"
                  :class="{ 'page-active': p === tablePagination.current_page }"
                  :disabled="p === '...'"
                  @click="p !== '...' && typeof p === 'number' && handlePageChange(p)"
                >{{ p }}</button>
                <button
                  class="page-btn next_btn"
                  :disabled="!tablePagination.has_next"
                  @click="handlePageChange(tablePagination.current_page + 1)"
                >»</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Role Creation / Edit Modal -->
  <BaseModal
    :id="modalId"
    :title="modalTitle"
    :visible="isModalOpen"
    @close="closeModal"
    @save="handleSave"
    :show-save-button="!isViewMode"
    size="lg"
  >
    <template #body>
      <div class="px-2">
        <!-- Role Title Form -->
        <BaseForm
          :fields="roleFormFields"
          v-model="formItemData"
          @submit="handleSave"
        />

        <!-- Permissions Section -->
        <div class="mt-4 pt-3 border-top">
          <h5 class="mb-3 d-flex align-items-center">
            <i class="fas fa-shield-alt me-2 text-primary"></i>
            Manage Permissions
          </h5>
          
          <div class="accordion" id="modalPermissionsAccordion" v-if="allPermissions.length">
            <div v-for="(perms, moduleName) in groupedPermissions" :key="moduleName" class="accordion-item shadow-none border mb-2">
              <h2 class="accordion-header">
                <button
                  class="accordion-button collapsed py-2 px-3 fw-bold"
                  type="button"
                  data-bs-toggle="collapse"
                  :data-bs-target="`#coll-mod-${moduleName.replace(/[^a-zA-Z0-9]/g, '')}`"
                >
                  {{ moduleName }} <span class="badge bg-info ms-2 text-white">{{ perms.length }}</span>
                </button>
              </h2>
              <div
                :id="`coll-mod-${moduleName.replace(/[^a-zA-Z0-9]/g, '')}`"
                class="accordion-collapse collapse"
                data-bs-parent="#modalPermissionsAccordion"
              >
                <div class="accordion-body p-0">
                  <div class="table-responsive">
                    <table class="table table-hover mb-0">
                      <thead class="table-light">
                        <tr>
                          <th class="ps-3 border-0">Permission</th>
                          <th class="text-center border-0" style="width: 80px">View</th>
                          <th class="text-center border-0" style="width: 80px">Create</th>
                          <th class="text-center border-0" style="width: 80px">Edit</th>
                          <th class="text-center border-0" style="width: 80px">Delete</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="perm in perms" :key="perm._id">
                          <td class="ps-3 align-middle border-0">
                            <span class="fw-500">{{ perm.title }}</span>
                          </td>
                          <td class="text-center align-middle border-0">
                            <input 
                              v-if="perm.is_view !== undefined && rolePermissionsMap[perm._id]"
                              type="checkbox" 
                              class="form-check-input" 
                              v-model="rolePermissionsMap[perm._id].is_view"
                              :disabled="isViewMode || !canUpdate('settings')"
                            />
                            <span v-else class="text-muted small">—</span>
                          </td>
                          <td class="text-center align-middle border-0">
                            <input 
                              v-if="perm.is_create !== undefined && rolePermissionsMap[perm._id]"
                              type="checkbox" 
                              class="form-check-input" 
                              v-model="rolePermissionsMap[perm._id].is_create"
                              :disabled="isViewMode || !canUpdate('settings')"
                            />
                            <span v-else class="text-muted small">—</span>
                          </td>
                          <td class="text-center align-middle border-0">
                            <input 
                              v-if="perm.is_update !== undefined && rolePermissionsMap[perm._id]"
                              type="checkbox" 
                              class="form-check-input" 
                              v-model="rolePermissionsMap[perm._id].is_update"
                              :disabled="isViewMode || !canUpdate('settings')"
                            />
                            <span v-else class="text-muted small">—</span>
                          </td>
                          <td class="text-center align-middle border-0">
                            <input 
                              v-if="perm.is_delete !== undefined && rolePermissionsMap[perm._id]"
                              type="checkbox" 
                              class="form-check-input" 
                              v-model="rolePermissionsMap[perm._id].is_delete"
                              :disabled="isViewMode || !canUpdate('settings')"
                            />
                            <span v-else class="text-muted small">—</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="text-center py-4 text-muted border rounded bg-light">
            <div class="spinner-border spinner-border-sm mb-2" v-if="crud.isLoading"></div>
            <p class="mb-0">{{ crud.isLoading ? 'Loading permissions...' : 'No permissions found.' }}</p>
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch, reactive } from "vue";
import { useCrud } from "@/composables/commen/useCrud";
import BaseModal from "@/components/commen/modal/BaseModal.vue";
import BaseForm, { FormField } from "@/components/commen/modal/BaseForm.vue";
import CommonTable2 from "@/components/commen/table/CommonTable2.vue";
import { usePermissions } from "@/composables/usePermissions";
const { canCreate, canUpdate } = usePermissions();
import apiService from "@/services/apiService";
import { toast, Content } from "vue3-toastify";

const ROLES_API = "/cms-roles-permissions/roles";
const PERMS_API = "/cms-roles-permissions/permissions";

const crud = useCrud(ROLES_API);

// --- State Management ---
const rows = ref<any[]>([]);
const isModalOpen = ref(false);
const isViewMode = ref(false);
const isEditMode = ref(false);
const formItemData = ref({ title: "" });
const currentRoleId = ref<string | null>(null);
const allPermissions = ref<any[]>([]);
const rolePermissionsMap = reactive<Record<string, any>>({});

const modalId = computed(() => isEditMode.value ? "editRoleModal" : "createRoleModal");
const modalTitle = computed(() => {
  if (isViewMode.value) return "View Role Details";
  if (isEditMode.value) return "Edit Role & Permissions";
  return "Create New Role";
});

const tableConfig = ref({
  columns: [
    { name: "Title", field: "title", sortable: true },
    { name: "Permissions Count", field: "perms_count", sortable: false },
    { name: "Created At", field: "createdAt", sortable: true },
  ],
  data: rows,
  actions: [true],
  loading: crud.isLoading,
  table_width: "max-content",
});

const tablePagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  total_pages: 0,
  has_next: false,
  has_prev: false,
});

const visiblePages = computed(() => {
  const total = tablePagination.value.total_pages;
  const current = tablePagination.value.current_page;
  if (!total || total <= 4) return Array.from({ length: Math.max(1, total) }, (_, i) => i + 1);
  const pages = [];
  pages.push(1);
  if (current > 4) pages.push("...");
  const windowStart = Math.max(2, current - 1);
  const windowEnd = Math.min(total - 1, current + 1);
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (current < total - 3) pages.push("...");
  pages.push(total);
  return pages;
});

const roleFormFields = computed<FormField[]>(() => [
  {
    key: "title",
    label: "Role Title",
    type: "text",
    required: true,
    placeholder: "Enter role title (e.g. Editor)",
    readonly: isViewMode.value,
  },
]);

// --- Watchers ---
watch(crud.data, (newVal) => {
  rows.value = (newVal || []).map((role: any) => ({
    ...role,
    perms_count: Array.isArray(role.permissions) ? role.permissions.filter((p: any) => 
        p.is_view || p.is_create || p.is_update || p.is_delete
    ).length : 0,
    createdAt: role.createdAt || role.created_at,
  }));
});

watch(() => crud.pagination.value, (newVal) => {
  if (!newVal) return;
  tablePagination.value.current_page = newVal.current_page || 1;
  tablePagination.value.total_pages = newVal.total_pages || 1;
  tablePagination.value.total = newVal.total || 0;
  tablePagination.value.has_prev = tablePagination.value.current_page > 1;
  tablePagination.value.has_next = tablePagination.value.current_page < tablePagination.value.total_pages;
}, { immediate: true, deep: true });

// --- Methods ---
const initPermissionsMap = (roleData?: any) => {
  // Clear the map
  Object.keys(rolePermissionsMap).forEach(key => delete rolePermissionsMap[key]);

  // 1. Initialize all from master list with false
  allPermissions.value.forEach(p => {
    rolePermissionsMap[p._id] = {
      is_view: false,
      is_create: false,
      is_update: false,
      is_delete: false
    };
  });

  // 2. Overlay role-specific permissions
  if (roleData && Array.isArray(roleData.permissions)) {
    roleData.permissions.forEach((rp: any) => {
      // Find matching permission from master list based on module ID or permission ID
      const rpModuleId = rp.cms_module_id?._id || rp.cms_module_id || rp._id;
      
      const masterPerm = allPermissions.value.find(mp => 
        (mp.cms_module_id?._id === rpModuleId) || 
        (mp.cms_module_id === rpModuleId) ||
        (mp._id === rpModuleId) ||
        (mp._id === rp._id)
      );

      const targetId = masterPerm ? masterPerm._id : rp._id;

      if (rolePermissionsMap[targetId]) {
         rolePermissionsMap[targetId].is_view = !!rp.is_view;
         rolePermissionsMap[targetId].is_create = !!rp.is_create;
         rolePermissionsMap[targetId].is_update = !!rp.is_update;
         rolePermissionsMap[targetId].is_delete = !!rp.is_delete;
      }
    });
  }
};

const groupedPermissions = computed(() => {
  const groups: Record<string, any[]> = {};
  
  // ALWAYS use master list for grouping so UI shows all modules!
  allPermissions.value.forEach(perm => {
    let moduleName = perm.module_name || perm.title || "General";
    
    // Check if module info exists in the master permission record or is directly available
    if (perm.cms_module_id && perm.cms_module_id.title) {
        moduleName = perm.cms_module_id.title;
    } else if (perm.title && !perm.module_name) {
        const guessed = perm.title.split(' Access')[0];
        moduleName = guessed;
    }

    if (!groups[moduleName]) groups[moduleName] = [];
    groups[moduleName].push(perm);
  });
  
  return groups;
});

const openCreateModal = () => {
  isViewMode.value = false;
  isEditMode.value = false;
  currentRoleId.value = null;
  formItemData.value = { title: "" };
  initPermissionsMap();
  isModalOpen.value = true;
};

const handleEdit = (row: any) => {
  isViewMode.value = false;
  isEditMode.value = true;
  currentRoleId.value = row._id;
  formItemData.value = { title: row.title };
  initPermissionsMap(row);
  isModalOpen.value = true;
};

const handleView = (row: any) => {
  isViewMode.value = true;
  isEditMode.value = true;
  currentRoleId.value = row._id;
  formItemData.value = { title: row.title };
  initPermissionsMap(row);
  isModalOpen.value = true;
};

const closeModal = () => {
  isModalOpen.value = false;
  isViewMode.value = false;
  isEditMode.value = false;
  currentRoleId.value = null;
  formItemData.value = { title: "" };
};

const handleSave = async () => {
  // Transform map back to array of objects
  const permissionsPayload = Object.entries(rolePermissionsMap).map(([permId, value]) => {
    // We need to find the module ID associated with this permission.
    // Try finding it in allPermissions (master list)
    const masterPerm = allPermissions.value.find(p => p._id === permId);
    
    // Resolve the module ID. It might be in p.cms_module_id._id or p.cms_module_id (as string)
    let moduleId = null;
    if (masterPerm) {
      moduleId = typeof masterPerm.cms_module_id === 'object' ? masterPerm.cms_module_id?._id : masterPerm.cms_module_id;
    }

    // Default to permId if module_id not found or if creating fallback
    if (!moduleId) {
        moduleId = permId; 
    }

    return {
      _id: moduleId,
      is_view: value.is_view,
      is_create: value.is_create,
      is_update: value.is_update,
      is_delete: value.is_delete,
    };
  });

  const payload = {
    title: formItemData.value.title,
    permissions: permissionsPayload
  };

  if (isEditMode.value && currentRoleId.value) {
    const result = await crud.updateItem(currentRoleId.value, payload);
    if (result) {
      closeModal();
      await crud.fetchData();
    }
  } else {
    const result = await crud.createItem(payload);
    if (result) {
      closeModal();
      await crud.fetchData();
    }
  }
};

const handleDelete = async (id: string) => {
  if (confirm("Are you sure you want to delete this role?")) {
    await crud.deleteItem(id);
    await crud.fetchData();
  }
};

const changeRows = async () => {
  await crud.fetchData({ page: 1, limit: tablePagination.value.per_page });
};

const handlePageChange = async (page: number) => {
  if (page < 1 || page === tablePagination.value.current_page) return;
  await crud.fetchData({ page, limit: tablePagination.value.per_page });
};

const fetchPermissions = async () => {
  try {
    const res = await apiService.get(PERMS_API);
    allPermissions.value = (res.data || res).filter((p: any) => !p.is_trash);
    initPermissionsMap(); // Pre-initialize mapping to avoid undefined templates
  } catch (err) {
    console.error("Error fetching permissions:", err);
  }
};

onMounted(async () => {
  await crud.fetchData();
  await fetchPermissions();
});
</script>

<script lang="ts">
export default {
  name: "CMSRolesAndPermissions",
};
</script>

<style scoped>
.rows-select { width: 82px; padding: 8px 17px; font-size: 0.85rem; background-color: transparent; }
.pagination-mini { display: flex; align-items: center; border-radius: 6px; overflow: hidden; }
.page-btn { border: none; background: transparent; font-size: 1rem; padding: 7px 12px; cursor: pointer; color: #6c757d; transition: 0.2s; border: 1px solid #dee2e6; outline: none; }
.page-btn:hover { background: #e9ecef; }
.page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.page-active { background: #21c1d6; color: #fff; border: 1px solid #21c1d6; font-weight: 600; }
.prev_btn, .next_btn { color: #21c1d6; }

.accordion-button:not(.collapsed) {
  background-color: #f8fdfd;
  color: #35bfbf;
  box-shadow: none;
}
.accordion-button:focus {
  box-shadow: none;
  border-color: rgba(0,0,0,.125);
}
.form-check-input:checked {
  background-color: #35bfbf;
  border-color: #35bfbf;
}
.fw-500 { font-weight: 500; }
.badge { font-weight: 500; padding: 0.4em 0.6em; }
</style>
